import PropTypes from 'prop-types';
// @mui
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, IconButton, Stack, Typography } from '@mui/material';
//
import Icon from '../Icon';
import {
  HIT,
  MOTION,
  RADIUS,
  UNIVERSE_FONT,
  focusRingSx,
  hairlineOf
} from 'src/theme/universe/brand';

// ---------------------------------------------------------------------------
// THE CONTEXT SHEET (DESIGN.md §14): below 900px the filters, a row's context
// and its actions rise from the bottom as a sheet — a temporary layer with a
// drag handle, a title row, and whatever the page puts in it. `full` makes it
// a full-height sheet for a comparison or a form. Glass is licensed here
// (stratum 3) and falls back to the opaque paper where blur is unsupported or
// reduced transparency is asked for.
// ---------------------------------------------------------------------------

ContextSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  title: PropTypes.node,
  meta: PropTypes.node,
  variant: PropTypes.oneOf(['bottom', 'full']),
  footer: PropTypes.node,
  children: PropTypes.node
};

export default function ContextSheet({
  open,
  onClose,
  title,
  meta,
  variant = 'bottom',
  footer,
  children
}) {
  const theme = useTheme();
  const light = theme.palette.mode === 'light';
  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      // a dialog's layer, not the drawer's: opened from a dialog (a finder's
      // Filters) the sheet rose BEHIND it; at the same level the later one wins
      sx={{ zIndex: theme.zIndex.modal }}
      transitionDuration={{ enter: MOTION.spatial, exit: MOTION.exit }}
      PaperProps={{
        sx: {
          maxHeight: variant === 'full' ? '100%' : '88vh',
          height: variant === 'full' ? '100%' : 'auto',
          borderRadius:
            variant === 'full'
              ? 0
              : `${RADIUS.overlay}px ${RADIUS.overlay}px 0 0`,
          bgcolor: light ? 'rgba(255,255,255,0.88)' : 'rgba(17,27,51,0.84)',
          backdropFilter: 'blur(12px) saturate(140%)',
          WebkitBackdropFilter: 'blur(12px) saturate(140%)',
          borderTop: `1px solid ${
            light ? 'rgba(1,38,109,0.12)' : 'rgba(255,255,255,0.16)'
          }`,
          boxShadow: light
            ? '0 -16px 40px -8px rgba(1,38,109,.20)'
            : '0 -20px 48px -10px rgba(0,0,0,.6)',
          backgroundImage: 'none',
          '@supports not (backdrop-filter: blur(1px))': {
            bgcolor: 'background.paper'
          },
          '@media (prefers-reduced-transparency: reduce)': {
            bgcolor: 'background.paper',
            backdropFilter: 'none'
          }
        }
      }}
    >
      <Box
        aria-hidden
        sx={{
          width: 36,
          height: 4,
          mx: 'auto',
          mt: 1,
          borderRadius: 2,
          bgcolor: hairlineOf(theme)
        }}
      />
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          px: 2,
          pt: 1,
          pb: 1.5,
          borderBottom: `1px solid ${hairlineOf(theme)}`
        }}
      >
        <Box sx={{ minWidth: 0, flexGrow: 1 }}>
          {title && (
            <Typography
              component="h2"
              noWrap
              sx={{
                fontFamily: UNIVERSE_FONT,
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.3,
                color: 'text.primary'
              }}
            >
              {title}
            </Typography>
          )}
          {meta && (
            <Typography
              component="div"
              noWrap
              sx={{
                fontFamily: UNIVERSE_FONT,
                fontSize: 12,
                fontWeight: 500,
                color: 'text.secondary'
              }}
            >
              {meta}
            </Typography>
          )}
        </Box>
        <IconButton
          onClick={onClose}
          aria-label="Close"
          sx={{
            width: HIT.touch,
            height: HIT.touch,
            borderRadius: `${RADIUS.button}px`,
            color: 'text.secondary',
            '&:focus-visible': focusRingSx(theme)
          }}
        >
          <Icon name="CloseCircle" size="md" />
        </IconButton>
      </Stack>
      <Box sx={{ overflowY: 'auto', px: 2, py: 2, minHeight: 0 }}>
        {children}
      </Box>
      {footer && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            borderTop: `1px solid ${hairlineOf(theme)}`,
            pb: 'calc(12px + env(safe-area-inset-bottom))'
          }}
        >
          {footer}
        </Box>
      )}
    </Drawer>
  );
}
